
import type { QueriesResponse } from '@empathybroker/tsclient-stats/dist/models/queries/queries.response'
import type { QueryData } from '@empathybroker/tsclient-stats/dist/models/queries/query-data'
import type { DataTableColumn, Pagination } from '../../../../models'
import { QueryTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/query-type.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { useKpis } from '../../../../composables/use-kpis'
import { AlignEnum, KpiEnum, QueriesTypeEnum } from '../../../../enums'
import { capitalizeSentence } from '../../../../filters'
import { ANALYTICS_CONSTS } from '../../../../store/analytics/analytics.consts'
import { CONFIG_CONSTS } from '../../../../store/config/config.consts'
import DataTable from '../../../crud/data-table.component.vue'
import PaginationBar from '../../../crud/pagination-bar.component.vue'
import KpiBar from '../../../kpi-bar.component.vue'
import Tabs from '../../../tabs.component.vue'

export default defineComponent({
  name: 'QueriesTable',
  components: {
    DataTable,
    KpiBar,
    PaginationBar,
    Tabs,
  },
  setup() {
    const { kpiFields } = useKpis()
    return {
      kpiFields,
    }
  },
  computed: {
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data,
      selectedId: ANALYTICS_CONSTS.getters.selectedId,
    }),
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    queriesTypeTabs(): any[] {
      return Object.values(QueriesTypeEnum).map(queryType => ({
        id: queryType,
        text: this.messages.queries.queriesTypes[queryType],
      }))
    },
    columns(): DataTableColumn[] {
      const kpisMessages = this.messages.kpis
      const columns: DataTableColumn[] = [
        {
          id: 'query',
          text: capitalizeSentence(this.messages.queryName),
          width: '200%',
        },
        {
          id: 'queryCount',
          text: capitalizeSentence(this.messages.quantity),
          align: AlignEnum.END,
        },
      ]
      if (this.selectedQueriesType === QueriesTypeEnum.ALL) {
        columns.push({
          id: 'queryNoResultsCount',
          text: capitalizeSentence(kpisMessages.noResults.valueLabel),
          align: AlignEnum.END,
        })
      }
      if (this.selectedQueriesType !== QueriesTypeEnum.WITHOUT_RESULTS) {
        if (this.kpis.includes(KpiEnum.CLICKS)) {
          columns.push({
            id: 'clickCountPct',
            text: capitalizeSentence(kpisMessages.clicks.percentageLabel),
            align: AlignEnum.END,
          })
          columns.push({
            id: 'successCountPct',
            text: capitalizeSentence(kpisMessages.findability.percentageLabel),
            align: AlignEnum.END,
          })
        }
        if (this.kpis.includes(KpiEnum.ADD2CART)) {
          columns.push({
            id: 'add2cartCountPct',
            text: capitalizeSentence(kpisMessages.add2cart.percentageLabel),
            align: AlignEnum.END,
          })
        }
        if (this.kpis.includes(KpiEnum.CHECKOUT)) {
          columns.push({
            id: 'checkoutCount',
            text: capitalizeSentence(kpisMessages.checkout.valueLabel),
            align: AlignEnum.END,
          })
        }
      }
      columns.push({
        id: 'volume',
        text: capitalizeSentence(this.messages.volume),
        width: '13rem',
        align: AlignEnum.CENTER,
      })
      return columns
    },
    queriesData(): QueryData[] {
      const queriesData: QueriesResponse = this.data.queries[this.selectedQueriesType]
      return queriesData ? queriesData.data : []
    },
  },
  data() {
    return {
      selectedQueriesType: QueriesTypeEnum.ALL,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
      },
      itemsPerPageOptions: [10, 50, 100, 200],
      percentageKpis: ['clickCountPct', 'successCountPct', 'add2cartCountPct'],
      maxValueToCompare: 0,
      loading: false,
      messages: this.$epb.messages.insights.analytics,
      QueriesTypeEnum,
    }
  },
  methods: {
    ...mapActions(ANALYTICS_CONSTS.moduleName, {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
      loadQueriesData: ANALYTICS_CONSTS.actions.loadQueriesData,
    }),
    async loadData(
      { resetPagination, resetAll } = { resetPagination: false, resetAll: false },
    ): Promise<void> {
      if (resetPagination || resetAll) {
        this.pagination.page = 1
      }
      if (resetAll) {
        this.selectedQueriesType = QueriesTypeEnum.ALL
      }
      this.loading = true
      try {
        const queriesData: QueriesResponse = await this.loadQueriesData({
          type: this.selectedQueriesType,
          pagination: this.pagination,
        })

        if (
          this.pagination.page === 1 &&
          this.selectedQueriesType === QueriesTypeEnum.ALL &&
          !!queriesData.data.length
        ) {
          this.maxValueToCompare = queriesData.data[0].queryCount
        }
        const queriesPaginationResponse = await this.fetchStatsReport({
          id: StatsReportsIdsEnum.QUERIES_PAGINATION,
          params: {
            type: this.getQueriesPaginationType(this.selectedQueriesType),
          },
        })
        this.pagination.totalItems = queriesPaginationResponse.data.termsCount
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loading = false
      }
    },
    getQueriesPaginationType(type: QueriesTypeEnum): QueryTypeEnum {
      return {
        [QueriesTypeEnum.ALL]: QueryTypeEnum.ALL,
        [QueriesTypeEnum.WITH_RESULTS]: QueryTypeEnum.WITH_RESULTS,
        [QueriesTypeEnum.WITHOUT_RESULTS]: QueryTypeEnum.WITHOUT_RESULTS,
      }[type]
    },
    getQuantity(type: QueriesTypeEnum, dataItem: QueryData): string {
      const quantity = {
        [QueriesTypeEnum.ALL]: dataItem.queryCount,
        [QueriesTypeEnum.WITH_RESULTS]: dataItem.resultsCount,
        [QueriesTypeEnum.WITHOUT_RESULTS]: dataItem.noResultsCount,
      }[type]
      return quantity.toLocaleString(this.$epb.locale)
    },
    getPercentageKpiText(kpi: number): string {
      return `${kpi.toLocaleString(this.$epb.locale, { maximumFractionDigits: 2 })}%`
    },
    getQueriesBar(dataItem: QueryData) {
      const kpiId =
        this.selectedQueriesType !== QueriesTypeEnum.WITHOUT_RESULTS
          ? KpiEnum.QUERIES
          : KpiEnum.NO_RESULTS
      return {
        id: kpiId,
        label: this.messages.volume,
        percentage: Math.round((dataItem.queryCount / this.maxValueToCompare) * 1000) / 10,
      }
    },
    onSelectionChange(selection: QueryData[]): void {
      this.$emit('querySelected', selection[0].query.toString() || [])
    },
    onPaginationChange(pagination: Pagination): void {
      this.pagination = pagination
      this.loadData()
    },
    async onDownload(pagination: Pagination): Promise<void> {
      this.$emit('updateLoading', { visible: true })
      try {
        await this.loadQueriesData({
          type: this.selectedQueriesType,
          pagination,
          download: true,
          downloadFields: this.columns
            .filter((column: DataTableColumn) => column.id !== 'volume')
            .map((column: DataTableColumn) => {
              let field = this.kpiFields ? this.kpiFields[column.id] : column.id
              if (field === 'queryCount' && this.selectedQueriesType !== QueriesTypeEnum.ALL) {
                field =
                  this.selectedQueriesType === QueriesTypeEnum.WITH_RESULTS
                    ? 'queryResultsCount'
                    : 'queryNoResultsCount'
              }
              return field
            }),
        })
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.$emit('updateLoading', { visible: false })
      }
    },
  },
  watch: {
    selectedQueriesType() {
      this.$refs.queriesTableContainer.scrollLeft = 0
      this.loadData({ resetPagination: true })
    },
    pagination: {
      deep: true,
      handler() {
        this.$refs.queriesTableContainer.scrollLeft = 0
      },
    },
  },
})
